import { useEffect, useRef, useState } from 'react';

export function useViewportReveal<T extends HTMLElement>(skipReveal = false) {
  const ref = useRef<T>(null);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  // Elements below the fold hide themselves (opacity: 0) and animate in once
  // observed. Everything else — including SSR/first paint, before this
  // effect has run — defaults to fully visible: without this, content would
  // render invisible in the server-rendered HTML and stay that way until
  // hydration, since neither state can be known server-side.
  const [shouldHideUntilRevealed, setShouldHideUntilRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || hasEnteredViewport) {
      return;
    }

    // Content a caller is about to programmatically scroll to (e.g. a chip
    // click loading a month below the fold) must render visible immediately
    // — the scroll itself proves it's "in view", and waiting for the
    // IntersectionObserver's next callback would otherwise land the jump on
    // still-hidden (opacity: 0) content for a moment.
    if (skipReveal) {
      setHasEnteredViewport(true);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setHasEnteredViewport(true);
      return;
    }

    if (element.getBoundingClientRect().top >= window.innerHeight) {
      setShouldHideUntilRevealed(true);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '0px 0px -14% 0px', threshold: 0.01 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [hasEnteredViewport, skipReveal]);

  return { ref, hasEnteredViewport, shouldHideUntilRevealed };
}
